import BaseCircularReactWrapperView, {
  BaseCircularReactWrapperViewOptions,
} from 'molbio/maps/circular/BaseCircularReactWrapperView';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import {SegmentsRingData} from 'molbio/maps/circular/CircularTypedefs';
import {getMinRings} from 'molbio/maps/circular/CircularUtil';
import {getLayers, LayersData} from 'molbio/maps/MapsUtil';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';

export interface SegmentsReactWrapperViewOptions<
  TLayout extends RingLayout,
  TSegmentsRingData extends SegmentsRingData,
> extends BaseCircularReactWrapperViewOptions<TLayout> {
  /**
   * All sequence features belonging to this view, regardless of whether they are currently inside the
   * viewport
   */
  data: Array<TSegmentsRingData>;
  /** A function to map from the coordinates of the cursor to the corresponding base pair */
  getHoverBase: (e: Event, round: boolean) => number;
  /** A function to determine which of the data fall within the viewport and should be rendered. */
  getRenderedData: (data: Array<TSegmentsRingData>) => Array<TSegmentsRingData>;
  /** Whether to recompute what data should be rendered on every redraw */
  invalidateDataOnRedraw?: boolean;
  /**
   * A function to actually render the React component.
   * @param basePairToAngle A linear function mapping an index in base pairs to an angle in radians on the
   *                        unit circle
   * @param data A list of sequence features to render
   * @param getHoverBase A function to map from the coordinates of the cursor to the corresponding base pair
   * @param layout The RingLayout object for the ring
   * @param radiusIn A function that accepts a count of ring heights away from the outermost ring (and towards
   *                 the center), and returns the amount of pixels that ring should be away from the center.
   * @param wrapperCid The cid of the CircularMap mapstate
   */
  renderReact: (
    basePairToAngle: d3.ScaleLinear<number, number>,
    data: Array<TSegmentsRingData>,
    getHoverBase: (e: Event, round: boolean) => number,
    layout: TLayout,
    mapstateCid: string,
    radiusIn: d3.ScaleLinear<number, number>
  ) => JSX.Element;
}

/**
 * A component that helps render a React component of an arbitrary number of layers as part of a RingView.
 * The sole extant consumer is the ORFs view; the search results view should be an easy conversion.
 * Theoretically, this can be used to convert children of LabeledSegmentsRingView as well - they will just
 * have to manage their own labels.
 */
export default class SegmentsReactWrapperView<
  TLayout extends RingLayout,
  TSegmentsRingData extends SegmentsRingData,
> extends BaseCircularReactWrapperView<TLayout> {
  data!: Array<TSegmentsRingData>;
  getHoverBase!: (e: Event, round: boolean) => number;
  getRenderedData!: (data: Array<TSegmentsRingData>) => Array<TSegmentsRingData>;
  invalidateDataOnRedraw!: boolean;
  recomputeLayers!: boolean;
  renderedData!: Array<TSegmentsRingData> | null;
  renderReact!: (
    basePairToAngle: d3.ScaleLinear<number, number>,
    data: Array<TSegmentsRingData>,
    getHoverBase: (e: Event, round: boolean) => number,
    layout: TLayout,
    mapstateCid: string,
    radiusIn: d3.ScaleLinear<number, number>
  ) => JSX.Element;
  rings!: number | null;

  initialize(options: SegmentsReactWrapperViewOptions<TLayout, TSegmentsRingData>): void {
    super.initialize(options);
    this.data = options.data;
    this.getHoverBase = options.getHoverBase;
    this.getRenderedData = options.getRenderedData;
    this.invalidateDataOnRedraw = options.invalidateDataOnRedraw ?? false;
    this.recomputeLayers = true;
    this.renderedData = null;
    this.renderReact = options.renderReact;
    this.rings = null;
  }

  /**
   * Calculates how many rings are needed to render all the data inside the viewport. This is used by
   * CircularMap to determine what the overall radius of the map should be.
   */
  computeMinRings(): number {
    return getMinRings(this.renderedData ?? this.data, this.layout.get('visible'));
  }

  getLayers(data: Array<TSegmentsRingData>): LayersData<TSegmentsRingData> {
    // We check data here and not renderedData because we still need to make radial space for rings with no
    // visible segments on screen so that segments do not jump when we rotate those rings' segments into view
    if (data.length === 0) {
      return {max: 0, layers: [], siblingMap: {}};
    }
    return getLayers(data, {sequence: this.model.get('sequence')});
  }

  invalidateData(): void {
    this.renderedData = null;
    this.invalidateLayers();
  }

  invalidateLayers(): void {
    this.recomputeLayers = true;
  }

  _renderComponentWrapper(): void {
    if (this.renderedData == null) {
      return;
    }
    renderToDOM(
      this.renderReact(
        this.basePairToAngle.bind(this),
        this.renderedData,
        this.getHoverBase,
        this.layout,
        this.model.cid,
        this.radiusIn
      ),
      this.el
    );
  }

  /**
   * Used by CircularMap to align each ring with respect to the others.
   * @param outerRadius The outer radius of this view
   */
  setConstraints(outerRadius: number): void {
    this.setOuterRadius(outerRadius);
  }

  /**
   * Given the number of rings this view should have, set its inner radius. This function implicitly assumes
   * it is being called _after_ `setConstraints` / that `this` has a meaningful `outerRadius` attribute.
   * @param rings The number of rings necessary to render all the layers in this view
   */
  storeBounds(rings: number | null): void {
    if (rings == null) {
      rings = this.rings;
    }
    this.rings = rings;
    const innerRadius = this.getOuterRadius() - this.layout.get('ringHeight') * this.rings!;
    this.layout.set('innerRadius', innerRadius);
    this.updateRadius();
  }

  update(): void {
    // Override entirely instead of calling super. We don't want to rerender until _after_ we adjust this
    // view's heights etc.
    if (!this.layout.get('visible')) {
      this.onHide?.();
      this.storeBounds(0);
    } else {
      if (this.renderedData == null || this.invalidateDataOnRedraw) {
        this.renderedData = this.getRenderedData(this.data);
      }
      if (this.recomputeLayers) {
        // n.b. `updateLayers` calls `storeBounds`
        this.updateLayers(this.renderedData);
      } else {
        // Even if we don't need to recompute layers, we still need to update the bounds.
        // Passing null for total rings will use the last value of total rings.
        this.storeBounds(null);
      }
    }

    this.renderComponentWrapper();
  }

  updateLayers(data: Array<TSegmentsRingData>): void {
    if (data.length === 0) {
      this.storeBounds(0);
      return;
    }
    const layerData = this.getLayers(data);
    for (const layer of layerData.layers) {
      layer.model.set('annularLayer', layer.layer);
    }
    this.storeBounds(layerData.max + 1);
  }
}
